/**
 * AISuggestRoutes — auto-plan multiple routes across 2W/4W/6W vehicles.
 *
 * Flow:
 *   1. Pick filters (LSGD, zones, wards) + target customer count.
 *   2. Adjust per-vehicle capacity caps.
 *   3. Set the shared start point + scheduled date.
 *   4. Click "Generate" → backend returns 1-3 preview routes (one per
 *      vehicle class), each with its own optimized stop order.
 *   5. Admin can either:
 *      a. "Save & Assign" a single suggested route → jumps to RouteCreate
 *         with that route pre-filled, OR
 *      b. "Save all as drafts" — persists every suggestion via
 *         /routes/ai-suggest/save; admin opens each later to assign.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkle, Truck, MapPin, Warning, CheckCircle, Path,
} from "@phosphor-icons/react";

const VEHICLE_META = {
  two_wheeler:  { label: "2-Wheeler",  color: "#7A5A11", bg: "#F9E4B7" },
  four_wheeler: { label: "4-Wheeler",  color: "#1F3A57", bg: "#DDE7F1" },
  six_wheeler:  { label: "6-Wheeler",  color: "#2D5A27", bg: "#D4E0D1" },
};

export default function AISuggestRoutes() {
  const nav = useNavigate();
  const [lsgds, setLsgds] = useState([]);
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);

  const [filters, setFilters] = useState({
    lsgd_id: "", zone_ids: [], ward_ids: [], total_count: 30,
  });
  const [caps, setCaps] = useState({ two_wheeler: 15, four_wheeler: 30, six_wheeler: 50 });
  const [start, setStart] = useState({ lat: "", lng: "", label: "" });
  const [scheduledDate, setScheduledDate] = useState(
    new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [engine, setEngine] = useState("haversine");
  const [prioritizeSR, setPrioritizeSR] = useState(true);
  const [srHandling, setSrHandling] = useState("link_only");

  const [suggestions, setSuggestions] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    api.get("/locations?type=lsgd").then(r => setLsgds(r.data || [])).catch(() => {});
    api.get("/zones").then(r => setZones(r.data || [])).catch(() => {});
    api.get("/locations?type=ward").then(r => setWards(r.data || [])).catch(() => {});
  }, []);

  const wardsForLsgd = useMemo(() =>
    wards.filter(w => w.parent_id === filters.lsgd_id), [wards, filters.lsgd_id]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => setStart({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "My location" }),
      () => toast.error("Could not get location"),
    );
  };

  const generate = async () => {
    if (!start.lat || !start.lng) { toast.error("Set a start point"); return; }
    setGenerating(true);
    setSuggestions(null);
    try {
      const body = {
        lsgd_id: filters.lsgd_id || null,
        zone_ids: filters.zone_ids,
        ward_ids: filters.ward_ids,
        total_count: filters.total_count,
        vehicle_caps: caps,
        scheduled_date: scheduledDate,
        start: { lat: Number(start.lat), lng: Number(start.lng), label: start.label },
        engine,
        prioritize_open_sr: prioritizeSR,
      };
      const r = await api.post("/routes/ai-suggest", body);
      setSuggestions(r.data);
      toast.success(`Generated ${r.data.suggestions?.length || 0} route(s) · ${r.data.total_selected} customers`);
    } catch (e) { toast.error(formatApiError(e)); }
    setGenerating(false);
  };

  const saveAll = async () => {
    if (!suggestions?.suggestions?.length) return;
    setSavingAll(true);
    try {
      const body = {
        scheduled_date: suggestions.scheduled_date,
        start: suggestions.start,
        end: suggestions.end,
        engine: suggestions.engine,
        filters: {
          lsgd_id: suggestions.filters.lsgd_id || "",
          zone_ids: suggestions.filters.zone_ids || [],
          ward_ids: suggestions.filters.ward_ids || [],
          service_frequency: null,
          vehicle_access: "any",
          limit: suggestions.total_selected || 200,
        },
        routes: suggestions.suggestions.map(s => ({
          vehicle_class: s.vehicle_class,
          vehicle_class_label: s.vehicle_class_label,
          customer_ids: s.customer_ids,
          name: `AI · ${s.vehicle_class_label} · ${suggestions.scheduled_date}`,
          stops: s.stops,
        })),
        sr_handling: srHandling,
      };
      const r = await api.post("/routes/ai-suggest/save", body);
      const good = (r.data.saved || []).filter(x => x.job_no).length;
      toast.success(`Saved ${good} route job(s) as drafts. Open Route List to assign.`);
      nav("/operations/routes");
    } catch (e) { toast.error(formatApiError(e)); }
    setSavingAll(false);
  };

  return (
    <AppShell title="AI Auto Route Suggestion"
      subtitle="Filter → let AI cluster by vehicle class → save all as drafts or assign per route"
      actions={
        <button onClick={() => nav("/operations/routes")}
                className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
          <ArrowLeft size={14}/>Back
        </button>
      }>
      <div className="grid grid-cols-12 gap-5">
        {/* Left column — filters + caps */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid="ai-filters">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-3 inline-flex items-center gap-1">
              <Sparkle size={12} weight="fill"/>Filters
            </div>
            <L>LSGD</L>
            <select value={filters.lsgd_id}
                    onChange={e => setFilters(f => ({ ...f, lsgd_id: e.target.value, zone_ids: [], ward_ids: [] }))}
                    data-testid="ai-lsgd" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs mb-3">
              <option value="">— any —</option>
              {lsgds.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <L>Health Zones</L>
            <MultiSelect options={zones} value={filters.zone_ids}
                          onChange={(v) => setFilters(f => ({ ...f, zone_ids: v }))}
                          testid="ai-zones"/>
            <L className="mt-2">Wards</L>
            <MultiSelect options={wardsForLsgd} value={filters.ward_ids}
                          onChange={(v) => setFilters(f => ({ ...f, ward_ids: v }))}
                          testid="ai-wards"/>

            <L className="mt-3">Target customer count</L>
            <div className="flex gap-2 items-center flex-wrap mb-2">
              {[20, 30, 50, 100].map(n => (
                <button key={n} onClick={() => setFilters(f => ({ ...f, total_count: n }))}
                        data-testid={`ai-count-${n}`}
                        className={`h-8 px-3 rounded-md text-xs border ${
                          filters.total_count === n
                            ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                            : "border-[#E5E2D9] bg-white text-[#5C5A55]"}`}>{n}</button>
              ))}
              <input type="number" min={1} max={300}
                     value={filters.total_count}
                     onChange={e => setFilters(f => ({ ...f, total_count: Number(e.target.value) || 30 }))}
                     data-testid="ai-count-custom"
                     className="h-8 w-24 px-2 border border-[#E5E2D9] rounded-md text-xs"
                     placeholder="Custom"/>
            </div>

            <label className="inline-flex items-center gap-2 text-[11px] text-[#5C5A55] mt-2 cursor-pointer">
              <input type="checkbox" checked={prioritizeSR}
                     onChange={e => setPrioritizeSR(e.target.checked)}
                     data-testid="ai-prioritize-sr"/>
              Prioritise customers with an open Service Request
            </label>
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid="ai-caps">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-3">
              Per-vehicle stop cap
            </div>
            {Object.entries(VEHICLE_META).map(([k, m]) => (
              <div key={k} className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-semibold w-24" style={{ color: m.color }}>
                  {m.label}
                </span>
                <input type="number" min={1} max={100}
                       value={caps[k]}
                       data-testid={`ai-cap-${k}`}
                       onChange={e => setCaps(c => ({ ...c, [k]: Number(e.target.value) || 1 }))}
                       className="flex-1 h-9 px-2 border border-[#E5E2D9] rounded-md text-xs"/>
                <span className="text-[10px] text-[#8A8780]">stops</span>
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-3">
              Start point + date
            </div>
            <L>Start Point (lat, lng)</L>
            <div className="flex gap-2 mb-2">
              <input value={start.lat} onChange={e => setStart(s => ({ ...s, lat: e.target.value }))}
                     placeholder="Lat" data-testid="ai-start-lat"
                     className="flex-1 h-9 px-2 border border-[#E5E2D9] rounded-md text-xs"/>
              <input value={start.lng} onChange={e => setStart(s => ({ ...s, lng: e.target.value }))}
                     placeholder="Lng" data-testid="ai-start-lng"
                     className="flex-1 h-9 px-2 border border-[#E5E2D9] rounded-md text-xs"/>
              <button onClick={useMyLocation} title="Use my location"
                      data-testid="ai-start-gps"
                      className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1">
                <MapPin size={11}/>GPS
              </button>
            </div>
            <L>Scheduled Date</L>
            <input type="date" value={scheduledDate}
                   onChange={e => setScheduledDate(e.target.value)}
                   data-testid="ai-date"
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-xs mb-2"/>
            <L>Optimizer</L>
            <select value={engine} onChange={e => setEngine(e.target.value)}
                    data-testid="ai-engine"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs mb-2">
              <option value="haversine">In-house (Haversine NN)</option>
              <option value="google">Google Directions API</option>
            </select>

            <L>Service Request handling</L>
            <select value={srHandling} onChange={e => setSrHandling(e.target.value)}
                    data-testid="ai-sr-handling"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs">
              <option value="link_only">Link open SRs</option>
              <option value="link_and_generate">Link open SRs + auto-generate for others</option>
              <option value="none">Skip — don&apos;t touch SRs</option>
            </select>
          </div>

          <button onClick={generate} disabled={generating}
                  data-testid="ai-generate-btn"
                  className="w-full h-11 rounded-md bg-[#C25D38] hover:bg-[#A54C2C] disabled:opacity-60 text-white font-semibold text-sm inline-flex items-center justify-center gap-2">
            <Sparkle size={14} weight="fill"/>
            {generating ? "Generating…" : "Generate AI Routes"}
          </button>
        </div>

        {/* Right column — suggestions */}
        <div className="col-span-12 lg:col-span-8 space-y-4">
          {!suggestions && !generating && (
            <div className="bg-white border border-dashed border-[#E5E2D9] rounded-md p-10 text-center text-sm text-[#8A8780]"
                 data-testid="ai-empty-state">
              <Sparkle size={22} className="mx-auto mb-2 text-[#C25D38]" weight="fill"/>
              Set your filters and click <b>Generate AI Routes</b> to see the suggested
              plan across 2W / 4W / 6W vehicles.
            </div>
          )}

          {suggestions && (
            <>
              <div className="bg-[#FAF8F2] border border-[#E5E2D9] rounded-md p-4 flex flex-wrap items-center gap-3"
                   data-testid="ai-summary">
                <div className="text-xs">
                  <b>{suggestions.suggestions?.length || 0}</b> route suggestion(s) ·
                  <b> {suggestions.total_selected}</b> customer(s) selected ·
                  scheduled for <b>{suggestions.scheduled_date}</b>
                </div>
                <button onClick={saveAll} disabled={savingAll}
                        data-testid="ai-save-all-btn"
                        className="ml-auto h-10 px-4 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center gap-2">
                  <CheckCircle size={13}/>
                  {savingAll ? "Saving…" : "Save all as drafts"}
                </button>
              </div>

              {suggestions.suggestions?.length === 0 && (
                <div className="bg-white border border-[#E5E2D9] rounded-md p-6 text-sm text-[#7A2E18] inline-flex items-center gap-2">
                  <Warning size={14}/>No customers matched — try widening your filters.
                </div>
              )}

              {(suggestions.suggestions || []).map((s) => {
                const m = VEHICLE_META[s.vehicle_class] || { label: s.vehicle_class, color: "#5C5A55", bg: "#EEEBE2" };
                return (
                  <div key={s.vehicle_class}
                       data-testid={`ai-route-card-${s.vehicle_class}`}
                       className="bg-white border border-[#E5E2D9] rounded-md p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-1 rounded-md text-[11px] font-bold"
                            style={{ color: m.color, background: m.bg }}>
                        <Truck size={11} className="inline mr-1"/>{m.label}
                      </span>
                      <div className="text-xs text-[#5C5A55]">
                        {s.stop_count}/{s.cap} stops · {s.distance_km} km · ~{Math.round(s.duration_min)} min
                      </div>
                      <div className="ml-auto">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">
                          <Path size={10}/>{suggestions.engine}
                        </span>
                      </div>
                    </div>
                    <ol className="space-y-1 max-h-60 overflow-y-auto pr-1">
                      {s.stops.slice(0, 30).map(stop => (
                        <li key={stop.seq} className="flex items-center gap-2 text-xs">
                          <span className="w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                                style={{ background: m.color }}>{stop.seq}</span>
                          <span className="font-mono text-[10px] text-[#8A8780] w-20 truncate">{stop.customer_code}</span>
                          <span className="flex-1 truncate">{stop.customer_name}</span>
                          <span className="text-[10px] text-[#8A8780]">{stop.ward_id || ""}</span>
                        </li>
                      ))}
                      {s.stops.length > 30 && (
                        <li className="text-[10px] text-[#8A8780] pl-6">
                          … and {s.stops.length - 30} more
                        </li>
                      )}
                    </ol>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

const L = ({ children, className = "" }) => (
  <label className={`text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2 ${className}`}>{children}</label>
);

const MultiSelect = ({ options, value, onChange, testid }) => {
  const toggle = (id) => {
    const s = new Set(value);
    s.has(id) ? s.delete(id) : s.add(id);
    onChange(Array.from(s));
  };
  return (
    <div className="border border-[#E5E2D9] rounded-md max-h-24 overflow-y-auto bg-white" data-testid={testid}>
      {options.length === 0 ? (
        <div className="p-2 text-[11px] text-[#8A8780]">— none —</div>
      ) : options.map(o => (
        <label key={o.id} className="flex items-center gap-2 px-2 py-1 text-[11px] hover:bg-[#FAF8F2] cursor-pointer">
          <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)}/>
          <span className="truncate">{o.name}</span>
        </label>
      ))}
    </div>
  );
};
